import React from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { useAdminOrganizerRequests } from '@hooks'

export type AdminTab = 'overview' | 'requests' | 'organizers' | 'events' | 'settings'

const TABS: { key: AdminTab; screen: string; label: string }[] = [
  { key: 'overview', screen: 'AdminHome', label: 'admin.nav.overview' },
  { key: 'requests', screen: 'AdminRequests', label: 'admin.nav.requests' },
  { key: 'organizers', screen: 'AdminOrganizers', label: 'admin.nav.organizers' },
  { key: 'events', screen: 'AdminEvents', label: 'admin.nav.events' },
  { key: 'settings', screen: 'AdminSettings', label: 'admin.nav.settings' },
]

/**
 * Navigation of the administration: Overview · Requests (with the pending
 * count) · Organizers · Events · Settings. Pills that wrap on narrow screens,
 * so nothing ever scrolls sideways at 320 px.
 */
export const AdminTabs: React.FC<{ active: AdminTab }> = ({ active }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const navigation = useNavigation<any>()
  const styles = getStyles(theme)
  const pending = useAdminOrganizerRequests({ limit: 1 }).data?.pendingCount ?? 0

  return (
    <View style={styles.row} accessibilityRole="tablist" testID="admin-tabs">
      {TABS.map((tab) => {
        const selected = tab.key === active
        return (
          <TouchableOpacity
            key={tab.key}
            accessibilityRole="tab"
            accessibilityState={{ selected }}
            aria-selected={selected}
            testID={`admin-tab-${tab.key}`}
            style={[styles.tab, selected && styles.tabSelected]}
            onPress={() => !selected && navigation.navigate(tab.screen)}
          >
            <Text
              type="bodySmallBold"
              color={selected ? theme.colors.white : theme.colors.secondary}
            >
              {t(tab.label)}
            </Text>
            {tab.key === 'requests' && pending > 0 ? (
              <View style={styles.badge} testID="admin-tab-requests-badge">
                <Text type="bodyExtraSmallBold" color={theme.colors.white}>
                  {pending}
                </Text>
              </View>
            ) : null}
          </TouchableOpacity>
        )
      })}
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    row: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.sm },
    tab: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      backgroundColor: theme.colors.white,
    },
    tabSelected: { backgroundColor: theme.colors.primary, borderColor: theme.colors.primary },
    badge: {
      minWidth: 18,
      height: 18,
      paddingHorizontal: 5,
      borderRadius: 9,
      backgroundColor: theme.colors.error,
      alignItems: 'center',
      justifyContent: 'center',
    },
  })
